/**
 * プロフィール画面の出入りに付ける View Transition の種別。
 * 値は index.css の `:active-view-transition-type()` と対になっている。
 */
export const PROFILE_VIEW_TRANSITION_TYPE = {
  FORWARD: "profile-forward",
  BACK: "profile-back",
} as const;

// アプリ内の「戻る」ボタン経由かどうか。左端スワイプの戻るは WebView 自身が
// 戻る演出を描くため、同じ動きを二重に見せないよう演出しない。
let isInAppBackRequested = false;

/** useSafeBack から呼ぶ。次の遷移1回だけ「アプリ内の戻る」として扱う */
export const markInAppBackNavigation = () => {
  isInAppBackRequested = true;
};

const consumeInAppBackNavigation = () => {
  const requested = isInAppBackRequested;
  isInAppBackRequested = false;
  return requested;
};

type TransitionLocation = {
  pathname: string;
  state: { __TSR_index?: number };
};

type LocationChangeInfo = {
  fromLocation?: TransitionLocation;
  toLocation: TransitionLocation;
};

// /profile/<userId> のみ。/profile/live-broadcaster/<id> 等は末尾セグメントが一致せず対象外になる。
// 先頭を固定しないのは、PRプレビューの basepath(/pr-123) が pathname に含まれるため。
const USER_PROFILE_PATHNAME = /\/profile\/[^/]+\/?$/;

const isUserProfilePathname = (pathname: string) =>
  USER_PROFILE_PATHNAME.test(pathname);

// 履歴インデックスの減少で「戻る」を判定する。ネイティブのスワイプバックも拾える
const isBackNavigation = (from: TransitionLocation, to: TransitionLocation) => {
  const fromIndex = from.state.__TSR_index;
  const toIndex = to.state.__TSR_index;
  if (typeof fromIndex !== "number" || typeof toIndex !== "number") {
    return false;
  }
  return toIndex < fromIndex;
};

/**
 * プロフィール画面への進入・離脱だけスライド演出の type を返す。
 * それ以外の遷移は false を返し、View Transition 自体を起こさない。
 */
export const resolveProfileViewTransitionTypes = ({
  fromLocation,
  toLocation,
}: LocationChangeInfo): Array<string> | false => {
  // フラグが次の遷移に持ち越されないよう、演出の可否に関わらず毎回消費する
  const isInAppBack = consumeInAppBackNavigation();

  if (!fromLocation || fromLocation.pathname === toLocation.pathname) {
    return false;
  }

  const isBack = isBackNavigation(fromLocation, toLocation);

  if (!isBack && isUserProfilePathname(toLocation.pathname)) {
    return [PROFILE_VIEW_TRANSITION_TYPE.FORWARD];
  }
  if (isBack && isInAppBack && isUserProfilePathname(fromLocation.pathname)) {
    return [PROFILE_VIEW_TRANSITION_TYPE.BACK];
  }
  return false;
};
